// NOTICE: KAPTION-INTEGRATION
// Composable for Sales Funnel Kanban data management
// Maintains independent local state per column, fetches via filter API

import { reactive, ref, computed } from 'vue';
import ConversationApi from 'dashboard/api/inbox/conversation.js';
import ConversationsAPI from 'dashboard/api/conversations.js';
import {
  FUNNEL_STAGES,
  FUNNEL_LABEL_PREFIX,
  ALL_FUNNEL_LABELS,
  getFunnelLabel,
  getConversationStage,
} from 'dashboard/constants/salesFunnel.js';

const PAGE_SIZE = 25;

export function useKaptionSalesKanban() {
  // Per-column reactive state
  const columnData = reactive({});
  const drawerConversation = ref(null);
  const isDrawerOpen = ref(false);

  // Initialize column data for all stages
  FUNNEL_STAGES.forEach(stage => {
    columnData[stage.key] = {
      conversations: [],
      loading: false,
      page: 1,
      hasMore: true,
      total: 0,
    };
  });

  /**
   * Build filter payload for a specific stage
   */
  function buildFilterPayload(stageKey, page = 1) {
    if (stageKey === 'new_lead') {
      // "New Lead" catches conversations WITHOUT any funnel label
      // Use not_equal_to for each funnel label (excluding new_lead itself)
      const otherLabels = ALL_FUNNEL_LABELS.filter(
        l => l !== getFunnelLabel('new_lead')
      );
      const payload = otherLabels.map((label, index) => ({
        attribute_key: 'labels',
        filter_operator: 'not_equal_to',
        values: [label],
        query_operator: index < otherLabels.length - 1 ? 'and' : undefined,
        attribute_model: 'standard',
      }));
      return {
        queryData: { payload },
        page,
      };
    }

    // For all other stages, filter by their specific funnel label
    return {
      queryData: {
        payload: [
          {
            attribute_key: 'labels',
            filter_operator: 'equal_to',
            values: [getFunnelLabel(stageKey)],
            attribute_model: 'standard',
          },
        ],
      },
      page,
    };
  }

  /**
   * Fetch conversations for a single column/stage
   */
  async function fetchColumn(stageKey, page = 1) {
    const col = columnData[stageKey];
    if (!col || col.loading) return;

    col.loading = true;
    try {
      const filterPayload = buildFilterPayload(stageKey, page);
      const { data } = await ConversationApi.filter(filterPayload);

      const conversations = data.payload || [];
      const meta = data.meta || {};

      if (page === 1) {
        col.conversations = conversations;
      } else {
        col.conversations = [...col.conversations, ...conversations];
      }

      col.page = page;
      col.total = meta.all_count || col.conversations.length;
      col.hasMore = conversations.length >= PAGE_SIZE;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[KanbanFunnel] Failed to fetch ${stageKey}:`, error);
    } finally {
      col.loading = false;
    }
  }

  /**
   * Load all columns
   */
  async function fetchAllColumns() {
    const promises = FUNNEL_STAGES.map(stage => fetchColumn(stage.key, 1));
    await Promise.allSettled(promises);
  }

  /**
   * Load more for a specific column
   */
  async function loadMore(stageKey) {
    const col = columnData[stageKey];
    if (!col || !col.hasMore || col.loading) return;
    await fetchColumn(stageKey, col.page + 1);
  }

  /**
   * Move a conversation between stages (API call only)
   * Note: vuedraggable already handles the local array mutation when dragging.
   * This function only updates totals and persists the label change via API.
   * On error, the caller should refresh columns to revert.
   */
  async function moveConversation(conversationId, fromStage, toStage) {
    if (fromStage === toStage) return;

    const fromCol = columnData[fromStage];
    const toCol = columnData[toStage];

    // Update totals (arrays already mutated by vuedraggable)
    if (fromCol) fromCol.total = Math.max(0, fromCol.total - 1);
    if (toCol) toCol.total += 1;

    try {
      // Get current labels
      const { data: labelsData } =
        await ConversationsAPI.getLabels(conversationId);
      const currentLabels = labelsData?.payload || [];

      // Remove any existing funnel labels, add the new one
      const newLabels = currentLabels.filter(
        l => !l.startsWith(FUNNEL_LABEL_PREFIX)
      );

      // Don't add a funnel label for 'new_lead' (it's the default/unlabeled state)
      if (toStage !== 'new_lead') {
        newLabels.push(getFunnelLabel(toStage));
      }

      await ConversationsAPI.updateLabels(conversationId, newLabels);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[KanbanFunnel] Failed to move conversation:', error);
      throw error;
    }
  }

  /**
   * Open the drawer for a conversation
   */
  function openDrawer(conversation) {
    drawerConversation.value = conversation;
    isDrawerOpen.value = true;
  }

  /**
   * Close the drawer and optionally refresh the affected column
   */
  function closeDrawer() {
    const conv = drawerConversation.value;
    isDrawerOpen.value = false;

    // Refresh the conversation's column (labels may have changed)
    if (conv) {
      const stage = getConversationStage(conv.labels || []);
      fetchColumn(stage, 1);
    }

    drawerConversation.value = null;
  }

  /**
   * Refresh a specific column
   */
  function refreshColumn(stageKey) {
    fetchColumn(stageKey, 1);
  }

  /**
   * Get computed column data for a stage
   */
  function getColumnData(stageKey) {
    return computed(
      () =>
        columnData[stageKey] || { conversations: [], loading: false, total: 0 }
    );
  }

  return {
    columnData,
    drawerConversation,
    isDrawerOpen,
    fetchAllColumns,
    fetchColumn,
    loadMore,
    moveConversation,
    openDrawer,
    closeDrawer,
    refreshColumn,
    getColumnData,
  };
}
